import { Component, computed, Inject, inject, OnInit, Signal } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { RouterModule } from '@angular/router';
import { CommentsStore, CommentsStoreType, NewsCommunitiesStore, NewsCommunitiesStoreType } from '@appState';
import { CommentsQueryParams } from '@models/index';
import {
  Choice,
  CuiButtonsModule,
  CuiCardModule,
  CuiDatepickerComponent,
  CuiFormComponent,
  CuiInputComponent,
  CuiSimpleSelectComponent,
  CuiTableModule,
  CuiValidators,
  formatToISODate,
  formatToISODateEndOfDay,
} from 'cui-components';

@Component({
  imports: [
    CuiTableModule,
    RouterModule,
    CuiCardModule,
    FormsModule,
    ReactiveFormsModule,
    CuiDatepickerComponent,
    CuiButtonsModule,
    CuiSimpleSelectComponent,
    CuiInputComponent,
    CuiFormComponent,
  ],
  template: `
    <cui-card>
      <cui-card-header [showCloseBtn]="true" (close)="onCancel()">
        <h2>Comments filter</h2>
      </cui-card-header>
      <cui-card-body>
        <cui-form [formGroup]="form" [trackChanges]="false">
          <div class="date-fields">
            <cui-date-picker
              [label]="'Start date'"
              formControlName="createdFromDateTime"
              [maxDate]="createdToControl?.value"
            ></cui-date-picker>
            <span class="flex-spacer"></span>
            <cui-date-picker
              [label]="'End date'"
              formControlName="createdToDateTime"
              [minDate]="createdFromControl?.value"
              [maxDate]="todaysDate"
            ></cui-date-picker>
            <cui-simple-select formControlName="communityId" [choices]="communities()" [label]="'Community'"></cui-simple-select>
            <cui-input formControlName="articleId" [label]="'Article ID'"></cui-input>
          </div>
        </cui-form>
      </cui-card-body>
      <cui-card-footer>
        <cui-button (clicked)="onCancel()">Cancel</cui-button>
        <cui-button-cta [disabled]="!form.valid || !form.dirty" (clicked)="onConfirm()">Submit</cui-button-cta>
      </cui-card-footer>
    </cui-card>
  `,
})
export class CommentsFiltersComponent implements OnInit {
  private newsCommunitiesStore: NewsCommunitiesStoreType = inject(NewsCommunitiesStore);
  private commentsStore: CommentsStoreType = inject(CommentsStore);
  private formBuilder: FormBuilder = inject(FormBuilder);
  private dialogRef: MatDialogRef<CommentsFiltersComponent> = inject(MatDialogRef<CommentsFiltersComponent>);

  communities: Signal<Choice[]> = computed(() => {
    const communities = this.newsCommunitiesStore.newsCommunities();
    const DEFAULT_CHOICE: Choice = { value: '', text: '<Any>' };
    return [
      DEFAULT_CHOICE,
      ...communities.map(community => ({
        value: community.id,
        text: community.name,
      })),
    ];
  });

  form!: FormGroup;
  todaysDate = new Date();
  initialFilterSettings?: CommentsQueryParams;

  constructor(@Inject(MAT_DIALOG_DATA) public data: CommentsQueryParams) {
    if (!this.newsCommunitiesStore.newsCommunities().length && !this.newsCommunitiesStore.isLoading()) {
      this.newsCommunitiesStore.getAll();
    }
  }

  ngOnInit(): void {
    const filterSettings = localStorage.getItem('commentsFilter');
    if (filterSettings) {
      this.initialFilterSettings = JSON.parse(filterSettings) as CommentsQueryParams;
      this.form = this.formBuilder.group({
        createdFromDateTime: [this.initialFilterSettings.createdFromDateTime, [CuiValidators.required]],
        createdToDateTime: [this.initialFilterSettings.createdToDateTime, [CuiValidators.required]],
        communityId: [this.initialFilterSettings.communityId],
        articleId: [this.initialFilterSettings.articleId],
      });
    }
  }

  get createdFromControl(): AbstractControl | null {
    return this.form.get('createdFromDateTime');
  }

  get createdToControl(): AbstractControl | null {
    return this.form.get('createdToDateTime');
  }

  onConfirm(): void {
    if (this.form.valid) {
      const commentsQuery: CommentsQueryParams = {
        ...this.form.value,
        createdFromDateTime: formatToISODate(new Date(this.form.value.createdFromDateTime)),
        createdToDateTime: formatToISODateEndOfDay(new Date(this.form.value.createdToDateTime)),
      };
      const hasDifferentArticleId = !!commentsQuery.articleId && commentsQuery.articleId !== this.initialFilterSettings?.articleId;
      const hasDifferentCommunityId = !!commentsQuery.communityId && commentsQuery.communityId !== this.initialFilterSettings?.communityId;
      this.commentsStore.submitFilterModal(commentsQuery);
      this.dialogRef.close({ commentsQuery, routeToList: hasDifferentArticleId || hasDifferentCommunityId });
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}
